<div
  *ngIf="block"
  class="lifemap h-full lg:px-5 2xl:px-0"
  [ngClass]="{
    'pointer-events-none blur': isStepCompleted() && !isLoading()
  }"
>
  <div *ngIf="isLoading()" class="flex h-full w-full items-center justify-center">
    <pl-loader />
  </div>

  <div *ngIf="!isLoading()" class="flex h-full">
    <div class="relative grow overflow-auto">
      <div class="absolute left-0 top-0 hidden w-[70px] lg:block">
        <h2 class="text-xs font-bold">
          {{ 'lifemap.your_lifemap' | translate }}
        </h2>
      </div>

      <div class="flex flex-col items-center lg:hidden">
        <h2 class="text-lg font-bold">
          {{ 'lifemap.your_lifemap' | translate }}
        </h2>
        <p class="uppercase">{{ 'lifemap.day' | translate }}</p>
        <div class="flex w-full justify-center gap-4 py-2">
          <div
            *ngFor="let day of daysDisplayMobile()"
            class="flex h-12 w-12 cursor-pointer items-center justify-center rounded-full text-lg text-pl-gray-450"
            (click)="selectDayForMobileDisplay(day.dayNumber)"
            [ngClass]="{ 'bg-pl-orange-500 font-bold text-white': day.selected }"
          >
            {{ day.dayNumber + 1 }}
          </div>
        </div>
      </div>

      <ng-template
        #plCalendarEventTemplate
        let-weekEvent="weekEvent"
        let-tooltipPlacement="tooltipPlacement"
        let-eventClicked="eventClicked"
        let-tooltipTemplate="tooltipTemplate"
        let-tooltipAppendToBody="tooltipAppendToBody"
        let-tooltipDisabled="tooltipDisabled"
        let-tooltipDelay="tooltipDelay"
        let-column="column"
        let-daysInWeek="daysInWeek"
      >
        <div
          class="cal-event"
          [ngStyle]="{
            color: weekEvent.event.color?.secondaryText,
            backgroundColor: weekEvent.event.color?.secondary
          }"
          [ngClass]="plEventsColor[weekEvent.event.meta?.type].cssClassForBorder"
          [mwlCalendarTooltip]="
            !tooltipDisabled
              ? (weekEvent.event.title
                | calendarEventTitle
                  : (daysInWeek === 1 ? 'dayTooltip' : 'weekTooltip')
                  : weekEvent.tempEvent || weekEvent.event)
              : ''
          "
          [tooltipPlacement]="tooltipPlacement"
          [tooltipEvent]="weekEvent.tempEvent || weekEvent.event"
          [tooltipTemplate]="tooltipTemplate"
          [tooltipAppendToBody]="tooltipAppendToBody"
          [tooltipDelay]="tooltipDelay"
          (mwlClick)="eventClicked.emit({ sourceEvent: $event })"
          (mwlKeydownEnter)="eventClicked.emit({ sourceEvent: $event })"
          tabindex="0"
          role="application"
          [attr.aria-label]="
            { event: weekEvent.tempEvent || weekEvent.event, locale: 'en-US' } | calendarA11y: 'eventDescription'
          "
        >
          <mwl-calendar-event-actions [event]="weekEvent.tempEvent || weekEvent.event"> </mwl-calendar-event-actions>
          &ngsp;
          <mwl-calendar-event-title
            [event]="weekEvent.tempEvent || weekEvent.event"
            [customTemplate]="plCalendarEventTitle"
            [view]="daysInWeek === 1 ? 'day' : 'week'"
          >
          </mwl-calendar-event-title>
        </div>
      </ng-template>

      <ng-template
        #plCalendarWeekViewHeader
        let-days="days"
        let-locale="locale"
        let-dayHeaderClicked="dayHeaderClicked"
        let-eventDropped="eventDropped"
        let-trackByWeekDayHeaderDate="trackByWeekDayHeaderDate"
        let-dragEnter="dragEnter"
      >
        <div class="cal-day-headers" role="row">
          <div
            class="cal-header"
            *ngFor="let day of days; let index = index; trackBy: trackByWeekDayHeaderDate"
            [class.cal-past]="day.isPast"
            [class.cal-today]="day.isToday"
            [class.cal-future]="day.isFuture"
            [class.cal-weekend]="day.isWeekend"
            [ngClass]="day.cssClass"
            (mwlClick)="dayHeaderClicked.emit({ day: day, sourceEvent: $event })"
            mwlDroppable
            dragOverClass="cal-drag-over"
            (drop)="
              eventDropped.emit({
                event: $event.dropData.event,
                newStart: day.date
              })
            "
            (dragEnter)="dragEnter.emit({ date: day.date })"
            tabindex="0"
            role="columnheader"
          >
            <p>{{ 'lifemap.day' | translate }}</p>
            <p>{{ index + 1 }}</p>
          </div>
        </div>
      </ng-template>

      <ng-template #plCalendarEventTitle let-event="event" let-view="view">
        <div
          class="flex h-full w-full gap-2"
          [ngStyle]="{
            color: event.color.secondaryText
          }"
          [ngClass]="(event | eventShorterThan60MinOrStartsAt2330) ? 'py-0' : 'py-2'"
        >
          <ng-container [ngSwitch]="event.meta.type">
            <ng-container *ngSwitchCase="LifemapType.sleep">
              <pl-icon [name]="PlIcon.moon" class="h-3.5 w-3.5 shrink-0" />
              <p class="text-inherit">{{ 'lifemap.category.sleep' | translate }}</p>
            </ng-container>

            <ng-container *ngSwitchCase="LifemapType.work">
              <pl-icon [name]="PlIcon.briefcase" class="h-3.5 w-3.5 shrink-0" />
              <p
                class="max-h-full min-w-0 grow overflow-y-hidden text-inherit"
                [ngClass]="(event | eventLongerThan60Min) ? 'whitespace-normal break-words' : 'truncate'"
              >
                {{ event.title }}
              </p>
            </ng-container>

            <ng-container *ngSwitchCase="LifemapType.nonWork">
              <pl-icon [name]="PlIcon.heart_wind" class="h-3.5 w-3.5 shrink-0" />
              <p
                class="max-h-full min-w-0 grow overflow-y-hidden text-inherit"
                [ngClass]="(event | eventLongerThan60Min) ? 'whitespace-normal break-words' : 'truncate'"
              >
                {{ event.title }}
              </p>
            </ng-container>

            <p
              *ngSwitchCase="LifemapType.addNewEntry"
              class="max-h-full min-w-0 grow self-center overflow-y-hidden text-center font-bold text-inherit"
              [ngClass]="(event | eventLongerThan60Min) ? 'whitespace-normal break-words' : 'truncate'"
            >
              <span *ngIf="event.meta.isSelected">{{ 'lifemap.new_entry' | translate }}</span>
              <span *ngIf="!event.meta.isSelected">
                + {{ block.data.config.entries.createNewText || 'lifemap.new_lifemap_entry' | translate }}
              </span>
            </p>

            <p
              *ngSwitchCase="LifemapType.blocked"
              class="max-h-full min-w-0 grow self-center overflow-y-hidden text-center text-inherit"
              [ngClass]="(event | eventLongerThan60Min) ? 'whitespace-normal break-words' : 'truncate'"
            >
              {{ event.title }}
            </p>
          </ng-container>
        </div>
      </ng-template>

      <ng-template
        #plCalendarViewHourSegment
        let-segment="segment"
        let-locale="locale"
        let-segmentHeight="segmentHeight"
        let-isTimeLabel="isTimeLabel"
        let-daysInWeek="daysInWeek"
      >
        <div
          *ngIf="isTimeLabel"
          [attr.aria-hidden]="{} | calendarA11y: (daysInWeek === 1 ? 'hideDayHourSegment' : 'hideWeekHourSegment')"
          class="cal-hour-segment relative !border-b-0"
          [style.height.px]="segmentHeight"
          [class.cal-hour-start]="segment.isStart"
          [class.cal-after-hour-start]="!segment.isStart"
          [ngClass]="segment.cssClass"
        >
          <div
            *ngIf="isTimeLabel && (segment.displayDate | calendarViewHourDisplay: timeFormat) !== MIDNIGHT"
            class="cal-time flex w-full items-center gap-2 !pt-0 text-sm !font-normal text-pl-gray-450"
            [ngStyle]="{ 'line-height': segmentHeight + 'px', transform: 'translateY(' + -(segmentHeight / 2) + 'px)' }"
          >
            <span class="block grow text-right">{{ segment.displayDate | calendarViewHourDisplay: timeFormat }}</span>
            <div class="w-[16px] min-w-[16px] border-t-2 border-[#e1e1e1]"></div>
          </div>
        </div>
      </ng-template>

      <mwl-calendar-week-view
        class="hidden lg:block"
        [viewDate]="viewDate()"
        [eventTemplate]="plCalendarEventTemplate"
        [events]="eventsToDisplay()"
        [refresh]="refresh"
        [validateEventTimesChanged]="validateEventTimesChanged.bind(this)"
        [hourSegmentHeight]="18"
        [minimumEventHeight]="9"
        [daysInWeek]="lifemapService.NUMBER_DAYS_LIFEMAP"
        [eventTitleTemplate]="plCalendarEventTitle"
        [hourSegmentTemplate]="plCalendarViewHourSegment"
        [headerTemplate]="plCalendarWeekViewHeader"
        (eventClicked)="onEventClicked($event.event)"
        (eventTimesChanged)="eventTimesChanged($event)"
      >
      </mwl-calendar-week-view>

      <mwl-calendar-day-view
        class="lg:hidden block mx-4"
        [viewDate]="viewDateMobile()"
        [refresh]="refresh"
        [eventTemplate]="plCalendarEventTemplate"
        [events]="eventsToDisplay()"
        [hourSegmentHeight]="18"
        [minimumEventHeight]="9"
        [eventTitleTemplate]="plCalendarEventTitle"
        [hourSegmentTemplate]="plCalendarViewHourSegment"
        (eventClicked)="onEventClickedMobile($event.event)"
      >
      </mwl-calendar-day-view>
    </div>
    <div class="relative hidden w-[450px] overflow-y-auto lg:block">
      <pl-lifemap-help @opacity *ngIf="!eventClicked()" [blockLifemap]="block" class="absolute w-full h-full" />
      <pl-edit-lifemap-entry
        class="absolute h-full w-full"
        @opacity
        *ngIf="eventClicked()"
        [allActivities]="allActivities()"
        [blockLifemap]="block"
        [event]="eventClicked()"
        [timeFormat]="timeFormat"
        (discardButtonClicked)="discardChangesOnEditEvent()"
        (formChanges)="onEditEntryUpdated($event)"
        (saveButtonClicked)="saveLifeMapEntry($event)"
      />
    </div>
  </div>
</div>
